import useSearchParams from "@/hooks/useSearchParams";
import { useAuthStore } from "@/lib/auth/AuthStore";
import { stepCodeLookup } from "@/components/edition/category/components/content/ProductListItem";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo } from "react";
import { Subjects } from "../../api/product";
import { BaseFilterKeyToLabel, SearchQueryKey, searchQueryKeys, SearchType } from "../../api/search";
import { getUserMeta } from "../../api/users";
const routerOptions = {
    shallow: true,
    scroll: true
};
const DEFAULT_PRODUCT_TYPE = SearchType.HANDOUT;
const DEFAULT_AVAILABLE = true;
const DEFAULT_SUBJECT = Subjects.EN;
const DEFAULT_SORT = "POPULAR";
export const useSearchFilterHandler = ()=>{
    const { push , pathname , query  } = useRouter();
    const searchParams = useSearchParams();
    const { isLoggedIn  } = useAuthStore();
    const { data: userMeta  } = useQuery([
        "search",
        "userMainSubject"
    ], getUserMeta, {
        enabled: isLoggedIn
    });
    const navigationHandler = useCallback((parameter)=>{
        push({
            pathname,
            query: parameter.toString()
        }, undefined, routerOptions);
    }, [
        push
    ]);
    const handleSortFilter = useCallback((sortKey)=>{
        searchParams.set(SearchQueryKey.SORT, sortKey);
        navigationHandler(searchParams);
    }, [
        searchParams
    ]);
    const handleResetFilter = useCallback(()=>{
        //productType 제외 모든 query 삭제
        for(const key in searchQueryKeys){
            searchParams.delete(searchQueryKeys[key]);
        }
        navigationHandler(searchParams);
    }, [
        searchParams
    ]);
    const handleSubjectFilter = useCallback((subject)=>{
        //productType 제외 모든 query 삭제
        for(const key in searchQueryKeys){
            searchParams.delete(searchQueryKeys[key]);
        }
        searchParams.set(SearchQueryKey.SUBJECT, subject);
        navigationHandler(searchParams);
    }, [
        searchParams
    ]);
    const handleDetailFilter = useCallback((filterType, id)=>{
        const isExist = searchParams.getAll(filterType).includes(id.toString());
        if (isExist) {
            const newFilter = searchParams.getAll(filterType).filter((item)=>item !== id.toString());
            searchParams.delete(filterType);
            if (newFilter.length > 0) {
                newFilter.map((item)=>searchParams.append(filterType, item));
            }
        } else {
            searchParams.append(filterType, id.toString());
        }
        push({
            pathname,
            query: searchParams.toString()
        }, undefined, routerOptions);
    }, [
        searchParams
    ]);
    const handleKeywordFilter = useCallback((value)=>{
        //전체 클릭시
        if (!value) {
            handleResetFilter();
            return;
        }
        //상세필터 초기화
        for(const key in searchQueryKeys){
            searchParams.delete(searchQueryKeys[key]);
        }
        searchParams.set(SearchQueryKey.RELATEDBOOKS, value);
        navigationHandler(searchParams);
    }, [
        searchParams
    ]);
    const findFilterKeyToLabel = useCallback((filterName)=>{
        return Object.keys(BaseFilterKeyToLabel).find((key)=>BaseFilterKeyToLabel[key] === filterName);
    }, []);
    const isCheckFilterItem = useCallback((filterName, filterItem)=>{
        return searchParams.getAll(filterName).includes(filterItem.toString());
    }, [
        searchParams
    ]);
    const isCheckFilterCount = useCallback((filterName)=>{
        return searchParams.getAll(findFilterKeyToLabel(filterName)).length;
    }, [
        searchParams
    ]);
    const selectedFilterList = useMemo(()=>{
        let selectedFilters = [];
        Object.values(SearchQueryKey).forEach((key)=>{
            if (key === SearchQueryKey.KEYWORD) return;
            if (key === SearchQueryKey.SORT) return;
            if (key === SearchQueryKey.SUBJECT) return;
            if (key === SearchQueryKey.PRODUCT_TYPE) return;
            searchParams.getAll(key).map((item)=>{
                // steps 필터의 경우 숫자를 텍스트로 변환
                const label = key === SearchQueryKey.STEPS ? stepCodeLookup[Number(item)] || item : undefined;
                selectedFilters.push({
                    key: key,
                    value: item,
                    label
                });
            });
        });
        return selectedFilters;
    }, [
        searchParams
    ]);
    const hasSelectedFilter = useMemo(()=>{
        return selectedFilterList.length > 0;
    }, [
        selectedFilterList
    ]);
    // solvookPasses 필터 추가
    const handleSolvookPassesFilter = useCallback((value)=>{
        if (value && searchParams.getAll(SearchQueryKey.SOLVOOKPASSES).includes(value)) {
            searchParams.delete(SearchQueryKey.SOLVOOKPASSES);
            navigationHandler(searchParams);
            return;
        }
        searchParams.set(SearchQueryKey.SOLVOOKPASSES, value);
        navigationHandler(searchParams);
    }, [
        searchParams
    ]);
    const filterParams = useMemo(()=>{
        var _searchParams_get, _ref, _searchParams_get1;
        return {
            q: searchParams.get(SearchQueryKey.KEYWORD),
            productType: DEFAULT_PRODUCT_TYPE,
            subject: (_ref = (_searchParams_get = searchParams.get(SearchQueryKey.SUBJECT)) !== null && _searchParams_get !== void 0 ? _searchParams_get : userMeta === null || userMeta === void 0 ? void 0 : userMeta.mainSubject) !== null && _ref !== void 0 ? _ref : DEFAULT_SUBJECT,
            available: DEFAULT_AVAILABLE,
            sort: (_searchParams_get1 = searchParams.get(SearchQueryKey.SORT)) !== null && _searchParams_get1 !== void 0 ? _searchParams_get1 : DEFAULT_SORT,
            filters: {
                curriculums: searchParams.getAll(SearchQueryKey.CURRICULUMS),
                grades: searchParams.getAll(SearchQueryKey.GRADES),
                publishers: searchParams.getAll(SearchQueryKey.PUBLISHERS),
                contentTypes: searchParams.getAll(SearchQueryKey.CONTENTTYPES),
                dataTypes: searchParams.getAll(SearchQueryKey.DATATYPES),
                units: searchParams.getAll(SearchQueryKey.UNITS),
                // editable: searchParams.get(SearchQueryKey.EDITABLES),
                authors: searchParams.getAll(SearchQueryKey.AUTHORS),
                genres: searchParams.getAll(SearchQueryKey.GENRES),
                eras: searchParams.getAll(SearchQueryKey.ERAS),
                relatedBooks: searchParams.getAll(SearchQueryKey.RELATEDBOOKS),
                tickets: searchParams.getAll(SearchQueryKey.TICKETS),
                solvookPasses: searchParams.getAll(SearchQueryKey.SOLVOOKPASSES),
                steps: searchParams.getAll(SearchQueryKey.STEPS)
            }
        };
    }, [
        searchParams,
        userMeta
    ]);
    // query에 ticket id가 넘어오는데 내가 보유하지 않은 이용권일 경우, query에서 제거
    useEffect(()=>{
        var _userMeta_tickets;
        if (!userMeta || !((_userMeta_tickets = userMeta.tickets) === null || _userMeta_tickets === void 0 ? void 0 : _userMeta_tickets.length)) return;
        const queryTicketIds = searchParams.getAll(SearchQueryKey.TICKETS);
        if (!queryTicketIds.length) return;
        const hasValidTicket = [
            ...userMeta.tickets,
            ...userMeta.solvookpluses,
            ...userMeta.freepasses
        ].filter((ticket)=>queryTicketIds.some((item)=>item === String(ticket.productInfo.id)));
        if (!hasValidTicket.length) {
            searchParams.delete(SearchQueryKey.TICKETS);
            navigationHandler(searchParams);
        }
    }, [
        userMeta,
        searchParams
    ]);
    return {
        handleSortFilter,
        handleResetFilter,
        handleSubjectFilter,
        handleDetailFilter,
        handleKeywordFilter,
        isCheckFilterItem,
        isCheckFilterCount,
        selectedFilterList,
        hasSelectedFilter,
        handleSolvookPassesFilter,
        filterParams
    };
};
